import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Workflow, GitBranch, Plug, Code, CheckCircle2 } from 'lucide-react';
import Card from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';
import { api } from '../lib/api';
import IndustryResearchAgeBadge from '../components/case-analytics/IndustryResearchAgeBadge';
import Step6DataFlowMapping from './design/Step6DataFlowMapping';
import Step7IntegrationPattern from './design/Step7IntegrationPattern';
import Step8CodeGeneration from './design/Step8CodeGeneration';

interface CaseContextEnvelope {
  caseId: string;
  industry?: { industryId?: string | null; name?: string | null } | null;
  company?: { companyId?: string | null; name?: string | null } | null;
}

// Step component type
interface StepConfig {
  number: number;
  title: string;
  icon: React.ReactNode;
  description: string;
}

const steps: StepConfig[] = [
  {
    number: 6,
    title: 'Data Flow Mapping',
    icon: <GitBranch className="w-5 h-5" />,
    description: 'Source to schema mapping',
  },
  {
    number: 7,
    title: 'Integration Pattern',
    icon: <Plug className="w-5 h-5" />,
    description: 'Configure ETL patterns',
  },
  {
    number: 8,
    title: 'Code Generation',
    icon: <Code className="w-5 h-5" />,
    description: 'Generate integration code',
  },
];

export const DesignWizard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const [currentStep, setCurrentStep] = React.useState<number>(6);

  // Track the previous caseId to detect changes
  const prevCaseIdRef = useRef<string | null>(null);

  // Reset state when entering with a new/different case
  useEffect(() => {
    if (caseId && caseId !== prevCaseIdRef.current) {
      console.log('🔄 Starting Design phase for case:', caseId);
      setCurrentStep(6);
      prevCaseIdRef.current = caseId;
    }
  }, [caseId]);

  // Fetch case data if caseId is provided
  const { data: caseData, isLoading: loadingCase, isFetchedAfterMount } = useQuery({
    queryKey: ['case', caseId],
    queryFn: async () => {
      if (!caseId) return null;
      const response = await api.get(`/cases/${caseId}`);
      return response;
    },
    enabled: !!caseId,
    // Always refetch on mount so the phase guard below sees the server's phase
    // rather than an entry another wizard cached within staleTime. One extra
    // GET per Design mount; without it the guard is inert while the entry is
    // fresh, which is exactly the window the post-completion bounce lived in.
    refetchOnMount: 'always',
  });

  // Resolve the linked industry so IndustryResearchAgeBadge can fetch its
  // refresh timestamp.  Same endpoint CaseDetail uses for industry context.
  const { data: caseContext } = useQuery<CaseContextEnvelope>({
    queryKey: ['case-context', 'envelope', caseId],
    queryFn: () => api.get<CaseContextEnvelope>(`/case-discovery/case-context/${caseId}`),
    enabled: !!caseId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
  const linkedIndustryId = caseContext?.industry?.industryId ?? null;

  // Verify case is in Design phase.
  // Act only on data fetched during THIS mount: the ['case', caseId] entry can
  // still hold currentPhase: 'Analysis' from the analysis wizard (staleTime
  // 5 min), and redirecting on that stale value bounced users to Step 0 right
  // after they completed the phase.
  useEffect(() => {
    if (isFetchedAfterMount && caseData && caseData.currentPhase !== 'Design') {
      // Redirect to appropriate phase
      if (caseData.currentPhase === 'Discovery') {
        navigate(`/cases/new-wizard?caseId=${caseId}`);
      } else if (caseData.currentPhase === 'Analysis') {
        navigate(`/cases/analysis-wizard?caseId=${caseId}`);
      }
    }
  }, [caseData, caseId, isFetchedAfterMount, navigate]);

  const handleNext = () => {
    if (currentStep < 8) {
      setCurrentStep(currentStep + 1);
    } else {
      // Design phase complete - navigate to dashboard or deployment
      navigate('/dashboard');
    }
  };

  const handleBack = () => {
    if (currentStep === 6) {
      // Go back to Analysis wizard
      navigate(`/cases/analysis-wizard?caseId=${caseId}`);
    } else {
      setCurrentStep(currentStep - 1);
    }
  };

  // Show loading spinner while fetching case data
  if (caseId && loadingCase) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-neutral-600">Loading case data...</p>
        </div>
      </div>
    );
  }

  // Show error if no caseId
  if (!caseId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600">No case ID provided</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 to-neutral-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Workflow className="w-8 h-8 text-purple-600" />
            <h1 className="text-4xl font-bold text-neutral-900">
              Design Phase
            </h1>
          </div>
          <p className="text-lg text-neutral-600">
            Integration Design & Code Generation for {caseData?.name || 'Case'}
          </p>

          {/* "Industry research is N days old" badge — renders only when the
              case has a linked industry AND the research has a known refresh
              timestamp.  Stays silent (returns null) for stub industries or
              first-of-its-kind generations to avoid flicker. */}
          {linkedIndustryId && (
            <div className="mt-3 flex justify-center">
              <IndustryResearchAgeBadge industryId={linkedIndustryId} />
            </div>
          )}
        </div>

        {/* Progress Indicator */}
        <Card className="mb-8 p-6 bg-white shadow-md">
          <div className="relative">
            {/* Progress Line */}
            <div className="absolute top-6 left-0 right-0 h-1 bg-neutral-200">
              <div
                className="h-full bg-purple-500 transition-all duration-500"
                style={{ width: `${((currentStep - 6) / (steps.length - 1)) * 100}%` }}
              />
            </div>

            {/* Steps */}
            <div className="relative flex justify-between">
              {steps.map((step) => {
                const isCompleted = currentStep > step.number;
                const isCurrent = currentStep === step.number;
                const isUpcoming = currentStep < step.number;

                return (
                  <div
                    key={step.number}
                    className="flex flex-col items-center"
                    style={{ width: `${100 / steps.length}%` }}
                  >
                    {/* Step Circle */}
                    <div
                      className={`
                        relative z-10 flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all
                        ${isCompleted ? 'bg-purple-500 border-purple-500 text-white' : ''}
                        ${isCurrent ? 'bg-white border-purple-500 text-purple-600 shadow-lg ring-4 ring-purple-100' : ''}
                        ${isUpcoming ? 'bg-white border-neutral-300 text-neutral-400' : ''}
                      `}
                    >
                      {isCompleted ? (
                        <CheckCircle2 className="w-6 h-6" />
                      ) : (
                        step.icon
                      )}
                    </div>

                    {/* Step Label */}
                    <div className="mt-3 text-center">
                      <p
                        className={`text-sm font-semibold ${
                          isCurrent ? 'text-purple-600' : isCompleted ? 'text-neutral-700' : 'text-neutral-400'
                        }`}
                      >
                        Step {step.number}: {step.title}
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          isCurrent ? 'text-neutral-600' : 'text-neutral-400'
                        }`}
                      >
                        {step.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* Current Step Content */}
        <div className="mb-8">
          <Card className="p-8 bg-white shadow-md">
            {currentStep === 6 && caseId && (
              <Step6DataFlowMapping
                caseId={caseId}
                caseData={caseData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {currentStep === 7 && caseId && (
              <Step7IntegrationPattern
                caseId={caseId}
                caseData={caseData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
            {currentStep === 8 && caseId && (
              <Step8CodeGeneration
                caseId={caseId}
                caseData={caseData}
                onNext={handleNext}
                onBack={handleBack}
              />
            )}
          </Card>
        </div>

        {/* Help Text */}
        <div className="text-center text-sm text-neutral-500">
          <p>
            The Design phase generates integration code and deployment artifacts for your Neo4j knowledge graph.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DesignWizard;
